/**
 * Employee sales: group × FY month matrix from sales_import_months lineChunks.
 * Loads EVERY month's lineChunks (not only weeklyLog weeks) so Jun/Jul and other
 * months are not skipped when week logs are incomplete.
 */
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import {
  COL_SALES_IMPORT_MONTHS,
  SALES_IMPORT_LINE_CHUNKS_SUB,
} from "../features/salesDataImport/constants";
import { salesImportMonthDocId } from "../features/salesDataImport/utils/weekUtils";
import { rowTranDateYmd } from "../features/salesDataImport/utils/dateParse";
import { normalizeSalesImportBranchId } from "../features/salesDataImport/config/salesImportBranchIds";
import { lineSaleAmount } from "../features/salesDataImport/services/salesLineChunksReader";
import {
  appBranchFromImportRow,
  isImportSaleVoucherRow,
} from "./salesImportTransactionDataService";
import {
  importRowEmployeeName,
  normalizeEmployeeName,
} from "./monthlyIncentivePartySalesService";
import {
  importRowSalespersonName,
  importRowStaffName,
} from "../features/salesDataImport/utils/importRowFields";
import {
  FY_MONTH_ORDER_SHORT,
  getFinancialYearMonthSequence,
} from "../utils/financialYear";
import {
  buildCommissionRateMap,
  resolveGroupCommissionPercent,
} from "./groupEmployeeCommissionService";
import {
  loadPartyGroupCommissionRateMap,
  resolvePartyGroupCommissionPercent,
} from "./partyEmployeeCommissionService";

const UNKNOWN_GROUP = "(Blank / unknown)";

function ymdPad(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Staff / employee / salesperson candidates from a line. */
export function rowEmployeeLabel(row) {
  const sp = importRowSalespersonName(row);
  if (sp) return sp;
  const emp = importRowEmployeeName(row);
  if (emp) return emp;
  return importRowStaffName(row);
}

/** All non-empty name fields for matching (staff OR employee may differ by month export). */
function employeeNameCandidates(row) {
  const names = [
    importRowSalespersonName(row),
    importRowStaffName(row),
    importRowEmployeeName(row),
    String(row?.employeeName ?? row?.EmployeeName ?? "").trim(),
    String(row?.staffName ?? row?.StaffName ?? "").trim(),
    String(row?.salespersonName ?? row?.SalespersonName ?? "").trim(),
  ];
  return [...new Set(names.map((n) => normalizeEmployeeName(n)).filter(Boolean))];
}

function rowMatchesEmployee(row, targetNorm) {
  if (!targetNorm) return false;
  return employeeNameCandidates(row).includes(targetNorm);
}

function parseLineAmount(row) {
  const sale = lineSaleAmount(row);
  if (Number.isFinite(sale) && sale !== 0) return Math.abs(sale);
  for (const key of ["amount", "Amount", "billAmount", "BillAmount", "netAmount", "NetAmount"]) {
    const raw = row?.[key];
    if (raw == null || raw === "") continue;
    const n = Number(String(raw).replace(/,/g, "").trim());
    if (Number.isFinite(n) && n !== 0) return Math.abs(n);
  }
  return 0;
}

function rawGroupFromRow(row) {
  return String(row?.itemGroup ?? row?.itemgroup ?? row?.["Item Group"] ?? row?.group ?? "").trim();
}

/** @returns {Promise<{ id: string, name: string, discount: number, employeeCommissionPercent: number|null }[]>} */
async function loadMasterGroupsFull() {
  try {
    const snap = await getDocs(collection(db, "groups"));
    return snap.docs
      .map((d) => {
        const data = d.data() || {};
        const name = String(data.name ?? data.gname ?? data.group ?? data.groupName ?? "").trim();
        return {
          id: d.id,
          name,
          discount: Number(data.discount || 0),
          employeeCommissionPercent:
            data.employeeCommissionPercent != null && data.employeeCommissionPercent !== ""
              ? Number(data.employeeCommissionPercent)
              : null,
        };
      })
      .filter((g) => g.name)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  } catch {
    return [];
  }
}

/** String names or group objects → name list for matching. */
function masterGroupNameList(masterGroups = []) {
  if (!masterGroups.length) return [];
  if (typeof masterGroups[0] === "string") return masterGroups;
  return masterGroups.map((g) => (typeof g === "string" ? g : g?.name)).filter(Boolean);
}

function matchMasterGroup(raw, masterGroups) {
  if (!raw) return null;
  const names = masterGroupNameList(masterGroups);
  const lower = raw.toLowerCase();
  for (const master of names) {
    if (master.toLowerCase() === lower) return master;
  }
  const textUpper = raw.toUpperCase();
  for (const master of [...names].sort((a, b) => b.length - a.length)) {
    const masterUpper = master.toUpperCase();
    if (textUpper === masterUpper || textUpper.includes(masterUpper) || masterUpper.includes(textUpper)) {
      return master;
    }
  }
  return raw;
}

function resolveGroupLabel(row, masterGroups = []) {
  const raw = rawGroupFromRow(row);
  if (!raw) return UNKNOWN_GROUP;
  if (!masterGroups.length) return raw;
  return matchMasterGroup(raw, masterGroups) || raw;
}

function scaleMonths(months, ratePct) {
  const f = Number(ratePct) / 100;
  if (!(f > 0)) return emptyMonthVector();
  return (months || emptyMonthVector()).map((v) => v * f);
}

function emptyMonthVector() {
  return Array.from({ length: 12 }, () => 0);
}

/**
 * Read ALL lineChunks for one calendar month (ignore weeklyLog — fixes missing Jun/Jul weeks).
 */
async function loadAllLineChunksForCalendarMonth(year, monthIndex) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const monthRef = doc(db, COL_SALES_IMPORT_MONTHS, monthId);
  const monthSnap = await getDoc(monthRef);
  if (!monthSnap.exists()) {
    return { monthId, exists: false, rows: [], chunkCount: 0 };
  }

  const colRef = collection(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  const rows = [];
  for (const d of snap.docs) {
    const part = d.data()?.rows;
    if (!Array.isArray(part)) continue;
    for (const row of part) {
      const branchId = normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? "");
      rows.push(branchId ? { ...row, branchId } : row);
    }
  }
  return { monthId, exists: true, rows, chunkCount: snap.size };
}

/**
 * Load full FY: all 12 month buckets, every lineChunk under each.
 * Optional employeeFilter keeps only that person's sale lines (faster / less memory).
 * @returns {Promise<{ rows: object[], monthStats: Array<object> }>}
 */
export async function loadPartySalesRowsForFy(
  fyStartYear,
  branchFilter = "all",
  onProgress,
  employeeFilter = ""
) {
  const seq = getFinancialYearMonthSequence(fyStartYear);
  const all = [];
  const monthStats = [];
  const targetEmp = normalizeEmployeeName(employeeFilter);

  for (let i = 0; i < seq.length; i += 1) {
    const { calendarYear, monthIndex, fySlot } = seq[i];
    const pack = await loadAllLineChunksForCalendarMonth(calendarYear, monthIndex);

    let saleCount = 0;
    let kept = 0;
    for (const row of pack.rows) {
      if (!isImportSaleVoucherRow(row)) continue;
      saleCount += 1;
      if (branchFilter && branchFilter !== "all" && appBranchFromImportRow(row) !== branchFilter) {
        continue;
      }
      if (targetEmp && !rowMatchesEmployee(row, targetEmp)) continue;

      all.push(row);
      kept += 1;
    }

    monthStats.push({
      fySlot,
      label: FY_MONTH_ORDER_SHORT[fySlot],
      calendarYear,
      monthIndex,
      monthId: pack.monthId,
      monthExists: pack.exists,
      chunkCount: pack.chunkCount,
      rawRows: pack.rows.length,
      saleRows: saleCount,
      kept,
    });

    onProgress?.(i + 1, 12, all.length, monthStats[monthStats.length - 1]);
  }

  return { rows: all, monthStats };
}

/** Distinct employee / salesperson names on sale lines. */
export function listEmployeesFromSaleRows(rows) {
  const set = new Set();
  for (const row of rows || []) {
    const label = rowEmployeeLabel(row);
    if (label) set.add(label);
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/**
 * Light scan: distinct employee names for FY × branch — does not keep sale lines in memory.
 * Months load in parallel so the dropdown fills faster.
 */
export async function listDistinctEmployeesForFy(fyStartYear, branchFilter = "all", onProgress) {
  const seq = getFinancialYearMonthSequence(fyStartYear);
  /** @type {Set<string>} */
  const names = new Set();
  let done = 0;

  await Promise.all(
    seq.map(async ({ calendarYear, monthIndex }) => {
      const pack = await loadAllLineChunksForCalendarMonth(calendarYear, monthIndex);
      for (const row of pack.rows || []) {
        if (!isImportSaleVoucherRow(row)) continue;
        if (branchFilter && branchFilter !== "all" && appBranchFromImportRow(row) !== branchFilter) {
          continue;
        }
        // Preferred display name + alternate import columns so the list matches filters.
        const label = rowEmployeeLabel(row);
        if (label) names.add(label);
        const staff = importRowStaffName(row);
        if (staff) names.add(staff);
        const emp = importRowEmployeeName(row);
        if (emp) names.add(emp);
      }
      done += 1;
      onProgress?.(done, seq.length, names.size);
    })
  );

  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** @deprecated */
export function listPartiesFromSaleRows(rows) {
  return listEmployeesFromSaleRows(rows);
}

/**
 * Group × month matrix for one employee. Always 12 FY columns.
 * Commission = sale × discount % (party×group settings; fall back to global group rate).
 * @param {object[]} [masterGroups]
 * @param {Awaited<ReturnType<typeof loadPartyGroupCommissionRateMap>>|null} [partyRatePack]
 */
export function buildPartyBrandMonthlyReport(
  rows,
  employeeName,
  fyStartYear,
  masterGroups = [],
  partyRatePack = null
) {
  const monthLabels = [...FY_MONTH_ORDER_SHORT];
  const globalRateMap =
    masterGroups.length && typeof masterGroups[0] === "object"
      ? buildCommissionRateMap(masterGroups)
      : new Map();

  const emptyReport = {
    employeeName: "",
    partyName: "",
    monthLabels,
    brands: [],
    groups: [],
    monthTotals: emptyMonthVector(),
    monthCommissionTotals: emptyMonthVector(),
    grandTotal: 0,
    grandCommission: 0,
    lineCount: 0,
    monthsWithData: 0,
    monthLineCounts: emptyMonthVector(),
    skippedNoDate: 0,
    skippedNoAmount: 0,
  };

  const target = normalizeEmployeeName(employeeName);
  if (!target) return emptyReport;

  /** @type {Map<string, number[]>} */
  const byGroup = new Map();
  const monthLineCounts = emptyMonthVector();
  let lineCount = 0;
  let skippedNoDate = 0;
  let skippedNoAmount = 0;
  const seq = getFinancialYearMonthSequence(fyStartYear);
  const validMonthKeys = new Set(seq.map((s) => `${s.calendarYear}-${s.monthIndex}`));

  for (const row of rows || []) {
    if (!rowMatchesEmployee(row, target)) continue;

    const ymd = rowTranDateYmd(row);
    if (!ymd || ymd.length < 7) {
      skippedNoDate += 1;
      continue;
    }
    const y = Number(ymd.slice(0, 4));
    const mi = Number(ymd.slice(5, 7)) - 1;
    if (!Number.isFinite(y) || mi < 0 || mi > 11) {
      skippedNoDate += 1;
      continue;
    }
    if (!validMonthKeys.has(`${y}-${mi}`)) continue;

    const slot = seq.findIndex((s) => s.calendarYear === y && s.monthIndex === mi);
    if (slot < 0) continue;

    const amt = parseLineAmount(row);
    if (!(amt > 0)) {
      skippedNoAmount += 1;
      continue;
    }

    const group = resolveGroupLabel(row, masterGroups);
    if (!byGroup.has(group)) byGroup.set(group, emptyMonthVector());
    byGroup.get(group)[slot] += amt;
    monthLineCounts[slot] += 1;
    lineCount += 1;
  }

  const groups = [...byGroup.entries()]
    .map(([group, months]) => {
      const total = months.reduce((s, v) => s + v, 0);
      const commissionPercent = resolvePartyGroupCommissionPercent(
        group,
        partyRatePack,
        globalRateMap,
        masterGroups
      );
      const commissionMonths = scaleMonths(months, commissionPercent);
      const commissionTotal = commissionMonths.reduce((s, v) => s + v, 0);
      return {
        group,
        brand: group,
        months,
        total,
        commissionPercent,
        commissionMonths,
        commissionTotal,
      };
    })
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total || a.group.localeCompare(b.group));

  const monthTotals = emptyMonthVector();
  const monthCommissionTotals = emptyMonthVector();
  for (const r of groups) {
    for (let i = 0; i < 12; i += 1) {
      monthTotals[i] += r.months[i];
      monthCommissionTotals[i] += r.commissionMonths[i];
    }
  }
  const grandTotal = monthTotals.reduce((s, v) => s + v, 0);
  const grandCommission = monthCommissionTotals.reduce((s, v) => s + v, 0);
  const monthsWithData = monthTotals.filter((v) => v > 0).length;
  const name = String(employeeName || "").trim();

  return {
    employeeName: name,
    partyName: name,
    monthLabels,
    brands: groups,
    groups,
    monthTotals,
    monthCommissionTotals,
    grandTotal,
    grandCommission,
    lineCount,
    monthsWithData,
    monthLineCounts,
    skippedNoDate,
    skippedNoAmount,
  };
}

/** Load master groups + party commission rates + FY sale lines for one employee. */
export async function fetchEmployeeGroupMonthlyReport({
  fyStartYear,
  employeeName = "",
  branchFilter = "all",
  onProgress,
}) {
  const masterGroupsP = loadMasterGroupsFull();
  const partyRatesP = employeeName
    ? loadPartyGroupCommissionRateMap(employeeName, fyStartYear)
    : Promise.resolve(null);
  const { rows, monthStats } = await loadPartySalesRowsForFy(
    fyStartYear,
    branchFilter,
    onProgress,
    employeeName
  );
  const [masterGroups, partyRatePack] = await Promise.all([masterGroupsP, partyRatesP]);
  const employees = listEmployeesFromSaleRows(rows);
  const report = buildPartyBrandMonthlyReport(
    rows,
    employeeName,
    fyStartYear,
    masterGroups,
    partyRatePack
  );
  return { rows, employees, masterGroups, report, monthStats, partyRatePack };
}

export { resolveGroupCommissionPercent };

export { ymdPad };
